import { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, Clock, Send } from 'lucide-react';
import SEO from '../components/SEO';

const content = {
  ar: {
    badge: 'تواصل معنا',
    h1: 'لنبني المستقبل معاً',
    sub: 'سواء كنت مهتماً بمنتجات كويدوري أو الحلول المؤسسية أو لديك سؤال — نحن هنا.',
    form: {
      name: 'الاسم الكامل',
      namePh: 'اسمك الكامل',
      email: 'البريد الإلكتروني',
      emailPh: 'your@email.com',
      company: 'الشركة (اختياري)',
      companyPh: 'اسم شركتك',
      subject: 'الموضوع',
      subjectPh: 'كيف يمكننا المساعدة؟',
      message: 'الرسالة',
      messagePh: 'أخبرنا عن احتياجاتك...',
      submit: 'إرسال الرسالة',
      success: 'تم إرسال رسالتك! سنرد في أقرب وقت.',
    },
    info: [
      { icon: Mail, label: 'راسلنا', value: 'support@quidory.com' },
      { icon: Clock, label: 'وقت الرد', value: 'خلال ٢٤ ساعة' },
    ],
  },
  en: {
    badge: 'Get in Touch',
    h1: "Let's Build Together",
    sub: "Whether you're interested in Quidory's products, enterprise solutions, or just have a question — we're here.",
    form: {
      name: 'Full Name',
      namePh: 'Your full name',
      email: 'Email Address',
      emailPh: 'your@email.com',
      company: 'Company (Optional)',
      companyPh: 'Your company name',
      subject: 'Subject',
      subjectPh: 'How can we help?',
      message: 'Message',
      messagePh: 'Tell us about your needs...',
      submit: 'Send Message',
      success: 'Your message has been sent! We will reply shortly.',
    },
    info: [
      { icon: Mail, label: 'Email Us', value: 'support@quidory.com' },
      { icon: Clock, label: 'Response Time', value: 'Within 24 hours' },
    ],
  },
};

const ContactPage = () => {
  const { language } = useLanguage();
  const c = content[language];
  const [form, setForm] = useState({ name: '', email: '', company: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  return (
    <div className="pt-16">
      <SEO
        titleEn="Contact Quidory — Get in Touch"
        titleAr="تواصل مع كويدوري"
        descEn="Contact the Quidory team for enterprise solutions, product inquiries, or general questions. We respond within 24 hours."
        descAr="تواصل مع فريق كويدوري للحلول المؤسسية أو استفساراتك. نرد خلال ٢٤ ساعة."
        path="/contact"
      />
      {/* Hero */}
      <section className="py-20 lg:py-28 bg-background relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.06)_0%,transparent_60%)]" />
        <div className="relative max-w-2xl mx-auto px-4 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-gold mb-4 block">{c.badge}</span>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-foreground mb-4">{c.h1}</h1>
          <p className="text-lg text-muted-foreground">{c.sub}</p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Info sidebar */}
            <div className="space-y-6">
              {c.info.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-start gap-4 p-5 rounded-xl border border-border bg-card">
                    <div className="p-2.5 rounded-lg bg-gold/10 shrink-0">
                      <Icon className="h-4 w-4 text-gold" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-0.5">{item.label}</p>
                      <p className="text-sm font-medium text-foreground">{item.value}</p>
                    </div>
                  </div>
                );
              })}
              <div className="p-5 rounded-xl border border-border bg-card">
                <p className="text-xs text-muted-foreground mb-2">
                  {language === 'ar' ? 'للاستفسارات المؤسسية' : 'For enterprise inquiries'}
                </p>
                <p className="text-sm font-medium text-foreground">enterprise@quidory.com</p>
              </div>
              <div className="p-5 rounded-xl border border-border bg-card">
                <p className="text-xs text-muted-foreground mb-2">
                  {language === 'ar' ? 'الكيان القانوني' : 'Legal Entity'}
                </p>
                <p className="text-sm font-medium text-foreground">Al Yakhour LLC</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {language === 'ar' ? 'وايومنغ، الولايات المتحدة' : 'Wyoming, USA'}
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              {sent ? (
                <div className="p-8 rounded-2xl border border-gold/30 bg-gold/5 text-center">
                  <div className="h-12 w-12 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-4">
                    <Send className="h-5 w-5 text-gold" />
                  </div>
                  <p className="text-foreground font-medium">{c.form.success}</p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  className="space-y-5"
                >
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">{c.form.name}</label>
                      <input
                        data-testid="contact-name"
                        type="text"
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                        placeholder={c.form.namePh}
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-border bg-card text-foreground placeholder:text-muted-foreground/60 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">{c.form.email}</label>
                      <input
                        data-testid="contact-email"
                        type="email"
                        value={form.email}
                        onChange={e => setForm({ ...form, email: e.target.value })}
                        placeholder={c.form.emailPh}
                        required
                        dir="ltr"
                        className="w-full px-4 py-2.5 rounded-lg border border-border bg-card text-foreground placeholder:text-muted-foreground/60 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-colors"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">{c.form.company}</label>
                      <input
                        data-testid="contact-company"
                        type="text"
                        value={form.company}
                        onChange={e => setForm({ ...form, company: e.target.value })}
                        placeholder={c.form.companyPh}
                        className="w-full px-4 py-2.5 rounded-lg border border-border bg-card text-foreground placeholder:text-muted-foreground/60 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">{c.form.subject}</label>
                      <input
                        data-testid="contact-subject"
                        type="text"
                        value={form.subject}
                        onChange={e => setForm({ ...form, subject: e.target.value })}
                        placeholder={c.form.subjectPh}
                        className="w-full px-4 py-2.5 rounded-lg border border-border bg-card text-foreground placeholder:text-muted-foreground/60 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">{c.form.message}</label>
                    <textarea
                      data-testid="contact-message"
                      rows={5}
                      value={form.message}
                      onChange={e => setForm({ ...form, message: e.target.value })}
                      placeholder={c.form.messagePh}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-border bg-card text-foreground placeholder:text-muted-foreground/60 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-colors resize-none"
                    />
                  </div>
                  <button
                    data-testid="contact-submit"
                    type="submit"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-foreground text-background font-medium text-sm hover:bg-foreground/90 transition-all duration-200 hover:-translate-y-0.5"
                  >
                    {c.form.submit}
                    <Send className="h-4 w-4" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
